"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckIcon,
  ChevronDownIcon,
  RefreshCwIcon,
  UploadCloudIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGeneratedDocumentStore } from "@/components/document/generatedDocumentStore";
import { useRouteContext } from "@/components/custom/contexts/routeContext";
import { apiAuthFetch, selectPraetorCase } from "@/lib/authFetch";
import { appendLocParam } from "@/lib/i18n";
import { ClientLogger } from "@/lib/clientLogger";
import { Spinner } from "@/components/ui/spinner";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type ActionState = "idle" | "saving" | "saved" | "error";
type Appearance = "icon" | "label";

/**
 * Single Save/Revise button used at every placement. The visible label, icon, tooltip,
 * and confirmation copy are driven by the tuple `(praetorDocumentId, praetorLinkAlive)`:
 *
 * | praetorDocumentId | linkAlive | action            | server call        |
 * | null              |   —       | Save to case      | caseAddDocument    |
 * | set               | true      | Add new revision  | caseUpdateDocument |
 * | set               | false     | Save to case      | caseAddDocument*   |
 *
 * *the stale-link fallback path: a successful caseAddDocument rewrites the local
 * `praetorDocumentId` (see `markVersionSavedToPraetor`) and re-establishes the link.
 *
 * `appearance="label"` renders a prominent labeled button (used by `DocumentActionBar`
 * in the PDF sidebar). When the doc has a live Praetor link, the labeled variant splits
 * into a button group: primary action = "Add new revision", chevron dropdown exposes a
 * secondary "Save as new document" override that flips `forceNew=true` on the request,
 * forcing the backend to take the fresh-add path even though the link is alive. The
 * forced-fresh save then overwrites the local `praetorDocumentId` with the newly minted
 * Praetor doc id (server-side, the original document keeps its prior revisions — we
 * just stop pointing at it from this generated doc).
 *
 * Architectural note: this is the *lazy reconciliation of a foreign-system reference*
 * pattern — verify the linked id on render, fall back to "create new" when it is
 * dead, re-link on success. Same shape as healing a stale FK pointer into an external
 * system. The backend resolves aliveness during `/api/documents` hydration so the
 * button can present the correct action on first render.
 */
export function SaveOrReviseButton({
  docId,
  compact = false,
  appearance = "icon",
}: {
  docId: string;
  compact?: boolean;
  appearance?: Appearance;
}) {
  const { t, i18n } = useTranslation();
  const { caseId } = useRouteContext();
  const [state, setState] = useState<ActionState>("idle");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selecting, setSelecting] = useState(false);
  // Carries the user's dropdown override from the "Save as new document" menu item
  // through the case-pick flow + confirmation dialog into the final POST body. Reset
  // to false on every fresh entry into the flow so a re-open of the primary action
  // never inherits a previous override.
  const [pendingForceNew, setPendingForceNew] = useState(false);
  // Holds the caseId obtained from selectCase() when no route caseId is preset.
  const selectedCaseIdRef = useRef<string | null>(null);

  const entry = useGeneratedDocumentStore((s) => s.entries[docId]);
  const praetorDocumentId = entry?.praetorDocumentId;
  const praetorLinkAlive = entry?.praetorLinkAlive === true;

  // The inline card has no version stepper, so `selectedVersionId` is typically undefined
  // there — fall back to the current (latest) version. The sidebar drives selection via
  // the stepper, so selectedVersionId is the right pick when present.
  const selectedVersionId = entry?.selectedVersionId ?? entry?.currentVersionId;
  const selectedVersion = entry?.versions?.find((v) => v.versionId === selectedVersionId);
  const selectedSavedAt = selectedVersion?.savedToPraetorAt ?? null;

  const mode: "revise" | "saveFresh" | "saveRelink" = useMemo(() => {
    if (!praetorDocumentId) return "saveFresh";
    return praetorLinkAlive ? "revise" : "saveRelink";
  }, [praetorDocumentId, praetorLinkAlive]);

  // When the user picks "Save as new document" from the dropdown, the dialog should
  // present saveFresh-style copy ("upload as a new file") instead of saveRevision
  // copy ("update the existing doc") — the override semantically becomes a fresh save.
  const effectiveMode: "revise" | "saveFresh" | "saveRelink" =
    pendingForceNew ? "saveFresh" : mode;

  const performSave = useCallback(async () => {
    const resolvedCaseId = caseId ?? selectedCaseIdRef.current;
    if (!resolvedCaseId || state === "saving") return;
    setState("saving");
    try {
      const res = await apiAuthFetch(appendLocParam(`/api/cases/${resolvedCaseId}/save-file`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ docId, versionId: selectedVersionId, forceNew: pendingForceNew }),
      });
      if (res.ok) {
        const data = (await res.json()) as { documentId?: string };
        if (data.documentId && selectedVersionId) {
          useGeneratedDocumentStore
            .getState()
            .markVersionSavedToPraetor(docId, selectedVersionId, data.documentId);
        }
        setState("saved");
      } else {
        ClientLogger.warn("Save to Praetor returned non-OK", {
          component: "SaveOrReviseButton",
          metadata: { docId, caseId: resolvedCaseId, status: res.status },
        });
        setState("error");
      }
    } catch (err) {
      ClientLogger.error("Save to Praetor failed", {
        error: err instanceof Error ? err : undefined,
        component: "SaveOrReviseButton",
        metadata: { docId, caseId, versionId: selectedVersionId, mode, forceNew: pendingForceNew },
      });
      setState("error");
    } finally {
      // Return to idle after the transient saved/error indicator so the button stays
      // clickable for the next save action.
      setTimeout(() => setState("idle"), 2000);
    }
  }, [docId, caseId, state, selectedVersionId, mode, pendingForceNew]);

  // Open the confirmation dialog for a primary (revision/fresh, depending on mode) or
  // forced-fresh save. Handles the "no preset case" path by routing through selectCase()
  // first; the dialog only opens once a caseId is in hand.
  const openConfirm = useCallback(
    (forceNew: boolean, e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (state === "saving" || selecting) return;
      setPendingForceNew(forceNew);
      if (caseId) {
        setConfirmOpen(true);
      } else {
        // No preset project context — ask the user to pick a case via the SDK.
        setSelecting(true);
        selectPraetorCase()
          .then((result) => {
            if (result?.id) {
              selectedCaseIdRef.current = result.id;
              setConfirmOpen(true);
            }
          })
          .catch((err) => {
            ClientLogger.error("selectCase failed", {
              error: err instanceof Error ? err : undefined,
              component: "SaveOrReviseButton",
            });
          })
          .finally(() => setSelecting(false));
      }
    },
    [caseId, state, selecting],
  );

  const onPrimaryClick = useCallback(
    (e?: React.MouseEvent) => openConfirm(false, e),
    [openConfirm],
  );

  const onSaveAsNewClick = useCallback(
    (e?: React.MouseEvent) => openConfirm(true, e),
    [openConfirm],
  );

  const onConfirm = useCallback(() => {
    setConfirmOpen(false);
    void performSave();
  }, [performSave]);

  const onConfirmOpenChange = useCallback((open: boolean) => {
    setConfirmOpen(open);
    if (!open) setPendingForceNew(false);
  }, []);

  const formatDate = (iso: string) => new Date(iso).toLocaleString(i18n.language);

  const label =
    mode === "revise"
      ? compact
        ? t("documentGenerator.addRevision")
        : t("pdfToolbar.addRevision")
      : compact
        ? t("documentGenerator.saveToPraetor")
        : t("pdfToolbar.saveToPraetor");

  const dialogTitle = effectiveMode === "revise" ? t("saveRevision.title") : t("saveFresh.title");
  const dialogBody =
    effectiveMode === "revise"
      ? selectedSavedAt
        ? t("saveRevision.bodyReupload", { savedAt: formatDate(selectedSavedAt) })
        : t("saveRevision.bodyReupload", { savedAt: "—" })
      : effectiveMode === "saveRelink"
        ? t("saveFresh.bodyRelinkRequired")
        : t("saveFresh.body");
  const confirmLabel = effectiveMode === "revise" ? t("saveRevision.confirm") : t("saveFresh.confirm");
  const cancelLabel = effectiveMode === "revise" ? t("saveRevision.cancel") : t("saveFresh.cancel");

  const isBusy = state === "saving" || selecting;

  const StateIcon =
    state === "saving" || selecting
      ? () => <Spinner size="sm" />
      : state === "saved"
        ? () => <CheckIcon className="size-3.5 text-green-500" />
        : state === "error"
          ? () => <AlertCircleIcon className="size-3.5 text-destructive" />
          : mode === "revise"
            ? () => <RefreshCwIcon className="size-3.5" />
            : () => <UploadCloudIcon className="size-3.5" />;

  return (
    <>
      {appearance === "label" ? (
        mode === "revise" ? (
          // Two-button split: primary revision + chevron dropdown for the
          // "Save as new document" override. Visually one unit (zero gap, shared
          // border radii at the seam) so it reads as a single labeled control.
          <div className="inline-flex items-stretch">
            <Button
              type="button"
              size="sm"
              onClick={onPrimaryClick}
              disabled={isBusy}
              className="rounded-r-none border-r border-primary-foreground/20"
            >
              <StateIcon />
              <span>{label}</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  disabled={isBusy}
                  aria-label={t("pdfToolbar.saveAsNewDocument")}
                  className="rounded-l-none px-2"
                >
                  <ChevronDownIcon className="size-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onSaveAsNewClick}>
                  <UploadCloudIcon className="size-3.5" />
                  <span>{t("pdfToolbar.saveAsNewDocument")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <Button type="button" size="sm" onClick={onPrimaryClick} disabled={isBusy}>
            <StateIcon />
            <span>{label}</span>
          </Button>
        )
      ) : compact ? (
        <button
          type="button"
          onClick={onPrimaryClick}
          disabled={isBusy}
          className="flex h-7 items-center gap-1.5 rounded-md border border-border/60 bg-background px-2.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50 cursor-pointer"
          title={label}
        >
          <StateIcon />
          <span>{label}</span>
        </button>
      ) : (
        <TooltipIconButton
          tooltip={label}
          className="size-7 rounded-md cursor-pointer hover:bg-muted"
          onClick={onPrimaryClick}
          disabled={isBusy}
        >
          <StateIcon />
        </TooltipIconButton>
      )}

      <Dialog open={confirmOpen} onOpenChange={onConfirmOpenChange}>
        <DialogContent showCloseButton>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangleIcon className="size-5 text-amber-500" />
              {dialogTitle}
            </DialogTitle>
            <DialogDescription>{dialogBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => onConfirmOpenChange(false)}>
              {cancelLabel}
            </Button>
            <Button onClick={onConfirm}>{confirmLabel}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
